import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import { Add } from '@mui/icons-material';
import { IconButton, Stack } from '@mui/material';

import Button from '../../common/Button';
import FormInput from '../form/FormInput';

import { AircraftAirportSortableItem } from './Aircraft/EditAircraftForm';

type AddFormProps = {
  defaultFormValue?: string[];
  data: string[];
  id: string;
  label?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  setData: (data: string[]) => void;
  handlePrimaryAction: (data: string[]) => void;
  handleSecondaryAction?: () => void;
};

const AddForm = ({
  id,
  label,
  data,
  setData,
  handlePrimaryAction,
  handleSecondaryAction,
  defaultFormValue,
  inputProps,
}: AddFormProps) => {
  const [items, setItems] = useState<string[]>([]);

  useEffect(() => {
    setItems(data);
  }, [data]);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const form = useForm({
    defaultValues: {
      [id]: '',
    },
  });

  const {
    handleSubmit,
    setError,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit: SubmitHandler<any> = (_) => {
    const value = watch(id);

    if (defaultFormValue?.includes(value.toLowerCase())) {
      setError(id, {
        message: `${value} is already exist.`,
      });
      return;
    }

    const commaItems = value.split(',').map((item) => item.trim());

    const items = Array.from(new Set(commaItems.filter((item) => item))).filter(
      (item) => !item.includes(',') && !defaultFormValue?.includes(item)
    );

    if (data.length === 0 && !value) {
      setError(id, {
        message: 'This field is required.',
      });
      return;
    }

    const result = Array.from(new Set([...data, ...items]));

    handlePrimaryAction(value.length ? result : data);
    setData([]);
  };

  const handleAdd = (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    const value = form.getValues()[id];

    if (defaultFormValue?.includes(value.toLowerCase())) {
      setError(id, {
        message: `${value} is already exist.`,
      });
      return;
    }

    const commaItems = value.split(',').map((item) => item.trim());

    const items = Array.from(new Set(commaItems.filter((item) => item))).filter(
      (item) => !item.includes(',')
    );

    if (value.length === 0) {
      setError(id, {
        message: 'This field is required.',
      });
      return;
    }

    setData(Array.from(new Set([...data, ...items])));
    reset();
    form.setValue(id, '');
  };

  const handleClearValue = (item: string) => {
    setData(data.filter((rec) => rec !== item));
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over?.id) {
        setItems((items) => {
          const oldIndex = items.indexOf(active.id.toString());
          const newIndex = items.indexOf(over.id.toString());

          const updateArray = arrayMove(items, oldIndex, newIndex);

          setData(updateArray);

          return updateArray;
        });
      }
    },
    [data]
  );

  return (
    <FormProvider {...form}>
      <form className="space-y-6">
        <FormInput
          required={data.length === 0}
          label={label}
          inputProps={{
            ...inputProps,
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAdd(e);
            }
          }}
          helperText={`Press comma (,) to type more ${label}.`}
          labelClassName="text-[#2196F3] font-semibold"
          name={id}
          endAdornment={
            <IconButton onClick={handleAdd} aria-label="add">
              <Add
                style={{
                  width: '16px',
                  height: '16px',
                  color: '#666666',
                }}
              />
            </IconButton>
          }
        />
        <div>
          <Stack
            direction="row"
            style={{
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={items} strategy={rectSortingStrategy}>
                {items.map((v) => {
                  if (!v) {
                    return;
                  }

                  return (
                    <AircraftAirportSortableItem
                      key={v}
                      id={v}
                      value={v}
                      handleClearValue={handleClearValue}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </Stack>
        </div>
        <div className="flex-end flex justify-end gap-3">
          <Button
            onClick={handleSecondaryAction}
            variant="outlined"
            tabIndex={3}
          >
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            onClick={handleSubmit((event) => onSubmit(event))}
            tabIndex={2}
            variant="contained_blue"
          >
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddForm;
